import React, { useCallback } from "react";
import { graphql } from "react-relay";

import { noop } from "lodash";

import ViewportContext from "~/common/ViewportContext";
import { addBlocker } from "~/lib/history";
import { useID } from "~/lib/hooks";
import { warning } from "~/lib/prompts";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { withSuspense } from "~/lib/suspense";
import { Button, LoadingSpinner, useModals } from "~/ui";
import Checkbox from "~/ui/legacy/Checkbox";
import { HelpCenterIcon, Modal } from "~/ui/legacyFellowIndex";

import { type Category, CategoryList, FixedCategory } from "./category";
import { TemplateCopy, TemplateCreate, TemplateEdit } from "./Edit";
import { CategoryEmptyState, NoSearchResults } from "./EmptyState";
import { TemplateGrid } from "./Grid";
import { TemplatePreview } from "./Preview";
import { SearchBox } from "./SearchBox";
import { filterTemplates } from "./utils";

import type { modalBrowseNoteTemplatesQuery } from "~/__generated__/modalBrowseNoteTemplatesQuery.graphql";
import type { modalPreviewTemplateQuery } from "~/__generated__/modalPreviewTemplateQuery.graphql";

import styles from "./css/modal.module.css";

export const NoteIdContext = React.createContext<string | undefined>(undefined);
NoteIdContext.displayName = "NoteIdContext";
export const ActionTextContext = React.createContext<string>("Apply");
ActionTextContext.displayName = "ActionTextContext";

const warnUnsaved = () =>
	warning("Changes you've made so far will not be saved.", {
		yesText: "Discard",
		noText: "Keep editing",
		title: "Discard unsaved changes?",
	});

export function useProtectedBackAndClose<
	Back extends (...args: never[]) => void,
	Close extends (...args: never[]) => void,
>(back: Back, close: Close) {
	const dirty = React.useRef(false);

	const setDirty = React.useCallback(() => (dirty.current = true), []);
	const setNotDirty = React.useCallback(() => (dirty.current = false), []);

	const [disablePrompt, setDisablePrompt] = React.useState(false);

	const goBack = React.useCallback(
		async (...args: Parameters<Back>) => {
			setDisablePrompt(true);
			if (!dirty.current) {
				back(...args);
			} else if (await warnUnsaved()) {
				back(...args);
			}
		},
		[back],
	);
	const doClose = React.useCallback(
		async (...args: Parameters<Close>) => {
			setDisablePrompt(true);
			if (!dirty.current) {
				close(...args);
			} else if (await warnUnsaved()) {
				close(...args);
			}
		},
		[close],
	);

	React.useEffect(() => {
		return addBlocker(() => {
			if (!disablePrompt && dirty.current) {
				return "You have unsaved changes. Are you sure you want to leave?";
			}
			return undefined;
		});
	}, [disablePrompt]);

	return [goBack, doClose, setDirty, setNotDirty] as const;
}

export function Edit({ close, templateId }: { close: (didEdit: boolean) => void; templateId: string }) {
	const [loading, setLoading] = React.useState(false);
	const formId = useID();
	const [, doClose, setDirty] = useProtectedBackAndClose(noop, close);
	const doCancel = React.useCallback(() => doClose(false), [doClose]);
	usePreventClose();

	return (
		<Modal onClose={doCancel} className={styles.modal} data-test-id="note-template-edit-modal">
			<Modal.Header onClose={doCancel}>
				<Modal.Title>Edit template</Modal.Title>
			</Modal.Header>
			<Modal.Body noPadding className={styles.modalBody}>
				<TemplateEdit
					formId={formId}
					templateId={templateId}
					// eslint-disable-next-line react/jsx-no-bind
					beforeSubmit={() => setLoading(true)}
					// eslint-disable-next-line react/jsx-no-bind
					afterSubmit={() => (setLoading(false), close(true))}
					onChange={setDirty}
				/>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="secondary" type="button" onClick={doCancel} disabled={loading}>
					Cancel
				</Button>
				<Button
					size="normal"
					variant="primary"
					type="submit"
					form={formId}
					disabled={loading}
					data-test-id="note-template-edit-save"
				>
					Save template
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export function Copy({ close, templateId }: { close: (newTemplateId: string | null) => void; templateId: string }) {
	const [loading, setLoading] = React.useState(false);
	const beforeSubmit = React.useCallback(() => setLoading(true), []);
	const afterSubmit = React.useCallback(
		(id: string | null) => {
			setLoading(false);
			close(id);
		},
		[close],
	);
	const formId = useID();
	const [, doClose, setDirty] = useProtectedBackAndClose(noop, close);
	const doCancel = React.useCallback(() => doClose(null), [doClose]);
	usePreventClose();

	return (
		<Modal onClose={doCancel} className={styles.modal}>
			<Modal.Header onClose={doCancel}>
				<Modal.Title>Copy template</Modal.Title>
			</Modal.Header>
			<Modal.Body noPadding className={styles.modalBody}>
				<TemplateCopy
					formId={formId}
					templateId={templateId}
					beforeSubmit={beforeSubmit}
					afterSubmit={afterSubmit}
					onChange={setDirty}
				/>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="secondary" type="button" onClick={doCancel} disabled={loading}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" type="submit" form={formId} disabled={loading}>
					Save template
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

interface BaseCreateProps {
	initialDoc?: { [key: string]: unknown };
	schemaVersion: number;
	initialTitle?: string | undefined;
	initialDescription?: string;
	initialTags?: readonly string[];
	parentId?: string;
	canEditVisibility?: boolean;
	hasAccessToPromptHeadings: boolean;
}

type CreateProps = BaseCreateProps &
	(
		| {
				offerToSetAsDefault: true;
				close: (createdTemplateInfo: { createdTemplateId: string; setAsDefault: boolean } | null) => void;
		  }
		| { offerToSetAsDefault?: false; close: (createdTemplateId: string | null) => void }
	);

export function Create({
	initialDoc,
	initialTitle,
	initialDescription,
	initialTags,
	parentId,
	canEditVisibility,
	hasAccessToPromptHeadings,
	schemaVersion,
	...rest
}: CreateProps) {
	const [loading, setLoading] = React.useState(false);
	const setAsDefaultRef = React.useRef<HTMLInputElement>(null);
	const checkboxId = useID();
	const formId = useID();
	const [, protectedClose, setDirty] = useProtectedBackAndClose(noop, rest.close);
	const doCancel = React.useCallback(() => protectedClose(null), [protectedClose]);
	const afterSubmit = React.useCallback(
		(createdId: string | null) => {
			if (createdId != null) {
				if (rest.offerToSetAsDefault) {
					rest.close({ createdTemplateId: createdId, setAsDefault: setAsDefaultRef.current?.checked ?? false });
				} else {
					rest.close(createdId);
				}
			} else {
				setLoading(false);
			}
		},
		[rest, setLoading],
	);
	usePreventClose();

	return (
		<Modal onClose={doCancel} className={styles.modal} data-test-id="note-template-create-modal">
			<Modal.Header onClose={doCancel}>
				<Modal.Title>Create template</Modal.Title>
			</Modal.Header>
			<Modal.Body noPadding className={styles.modalBody}>
				<TemplateCreate
					formId={formId}
					// eslint-disable-next-line react/jsx-no-bind
					beforeSubmit={() => setLoading(true)}
					afterSubmit={afterSubmit}
					onChange={setDirty}
					initialDoc={initialDoc}
					initialTitle={initialTitle}
					initialDescription={initialDescription}
					initialTags={initialTags}
					parentId={parentId}
					canEditVisibility={canEditVisibility}
					hasAccessToPromptHeadings={hasAccessToPromptHeadings}
					schemaVersion={schemaVersion}
				/>
			</Modal.Body>
			<Modal.Footer>
				{rest.offerToSetAsDefault && (
					<Modal.FooterSecondaryActions className={styles.footerActions}>
						<label htmlFor={checkboxId} className={styles.defaultForStreamLabel}>
							<Checkbox
								green
								id={checkboxId}
								innerRef={setAsDefaultRef}
								defaultChecked
								className={styles.defaultTemplateCheckbox}
							/>
							<span className={styles.text}>Make this the default template for this series</span>
						</label>
					</Modal.FooterSecondaryActions>
				)}
				<Button size="normal" variant="secondary" type="button" onClick={doCancel} disabled={loading}>
					Cancel
				</Button>
				<Button
					size="normal"
					variant="primary"
					type="submit"
					form={formId}
					disabled={loading}
					data-test-id="note-template-create-save"
				>
					Save template
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

function LoadingFallback() {
	return <LoadingSpinner delay={500} />;
}

function PreviewInner({
	back,
	templateId,
	setPreviewing,
	close,
}: {
	templateId: string;
	back?: () => void;
	setPreviewing: (id: string) => void;
	close: (templateId?: string) => void;
}) {
	const { isMobile } = React.useContext(ViewportContext);
	const props = useLazyLoadQuery<modalPreviewTemplateQuery>(
		graphql`
			query modalPreviewTemplateQuery($templateId: ID!) {
				node(id: $templateId) {
					__typename

					... on NoteTemplateNode {
						id
						viewerCanEdit
						...PreviewNoteTemplate
					}
				}
			}
		`,
		{ templateId },
		{ fetchPolicy: "network-only" },
	);
	const modalCtx = useModals();
	const selectTemplate = React.useCallback(() => close(templateId), [close, templateId]);

	const actionText = React.useContext(ActionTextContext);

	const closeModal = React.useCallback(() => close(), [close]);

	if (props.node?.__typename !== "NoteTemplateNode") throw new Error("Invalid note template ID");
	const template = props.node;

	const edit = useCallback(
		() => modalCtx.open<boolean>(({ close }) => <Edit templateId={template.id} close={close} />),
		[modalCtx, template.id],
	);

	const copy = useCallback(async () => {
		const newTemplateId = await modalCtx.open<string | null>(({ close }) => (
			<Copy templateId={template.id} close={close} />
		));

		if (newTemplateId != null) setPreviewing(newTemplateId);
	}, [modalCtx, template.id, setPreviewing]);

	return (
		<>
			<Modal.Header onClose={closeModal}>
				<Modal.Title>Template Preview</Modal.Title>
			</Modal.Header>
			<Modal.Body noPadding dark>
				<TemplatePreview template={template} />
			</Modal.Body>
			<Modal.Footer>
				{back && (
					<Modal.FooterSecondaryActions>
						<Button size="normal" onClick={back}>
							Back
						</Button>
					</Modal.FooterSecondaryActions>
				)}
				{isMobile || (
					<Button
						size="normal"
						variant="secondary"
						onClick={template.viewerCanEdit ? edit : copy}
						data-test-id="note-template-modal-edit-button"
					>
						{template.viewerCanEdit ? "Edit" : "Copy to My templates"}
					</Button>
				)}
				<Button
					size="normal"
					variant="primary"
					onClick={selectTemplate}
					data-test-id="note-template-modal-apply-button"
				>
					{actionText}
				</Button>
			</Modal.Footer>
		</>
	);
}

const Preview = withSuspense(<LoadingFallback />, PreviewInner);

graphql`
	fragment modalBrowseTemplate on NoteTemplateNode @relay(mask: false) {
		title
		starred
		summary
		tags
		updatedAt
		viewerAppliedAt
		visibility
		author {
			... on TemplateUserAuthor {
				name
			}
			... on TemplateCompanyAuthor {
				name
			}
			... on TemplateIconAuthor {
				iconName: name
			}
			... on Account {
				name
			}
			... on ExternalAccount {
				name
			}
			... on User {
				fullName
			}
		}
		...GridNoteTemplates
	}
`;

type BrowseProps = { close: (templateId?: string) => void; defaultSelected?: string; defaultCategory?: Category } & (
	| { mode?: "browse" }
	| { mode: "preview"; templateId: string; hideBackButton?: boolean }
);

function BrowseInner({ defaultCategory, defaultSelected, close, ...props }: BrowseProps) {
	const { isMobile } = React.useContext(ViewportContext);

	const queryResult = useLazyLoadQuery<modalBrowseNoteTemplatesQuery>(
		graphql`
			query modalBrowseNoteTemplatesQuery {
				viewer {
					id # for relay mutation configs
					canCreateWorkspaceTemplate
					account {
						billingAccount {
							subscriptionProductName
							hasAccessToWorkspaceTemplates: hasAccessToFeature(feature: templates)
							hasAccessToPromptHeadings: hasAccessToFeature(feature: prompt_headings)
						}
					}

					noteTemplates(first: null) @connection(key: "Browse_noteTemplates") {
						edges {
							node {
								id
								...modalBrowseTemplate @relay(mask: false)
							}
						}
					}
				}
			}
		`,
		{},
		{ fetchPolicy: "network-only" },
	);

	const noteTemplates = queryResult.viewer.noteTemplates.edges.map(edge => edge.node);

	const scrollPosition = React.useRef(0);
	const [performScroll, setPerformScroll] = React.useState(false);
	const scrollRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (performScroll) {
			setPerformScroll(false);
			scrollRef.current?.scrollTo(0, scrollPosition.current);
		}
	}, [performScroll, scrollPosition]);

	const [category, setCategory] = React.useState<Category>(defaultCategory || FixedCategory.ALL);
	const [previewing, setPreviewing] = React.useState<string | undefined>(
		props.mode === "preview" ? props.templateId : undefined,
	);
	const closeModal = React.useCallback(() => close(), [close]);
	const [search, setSearch] = React.useState("");
	const setSearchFromEvent = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value),
		[setSearch],
	);
	const clearSearch = React.useCallback(() => setSearch(""), [setSearch]);
	const filteredTemplates = React.useMemo(() => {
		return filterTemplates(noteTemplates, category, search, defaultSelected);
	}, [category, search, noteTemplates, defaultSelected]);

	const allTags = React.useMemo(() => {
		const tags: Set<string> = new Set();
		noteTemplates?.forEach(template => {
			template.tags.forEach(t => tags.add(t));
		});
		return Array.from(tags.values());
	}, [noteTemplates]);

	const haveSearch = !!search;
	React.useEffect(() => {
		if (haveSearch) setCategory(FixedCategory.ALL);
	}, [haveSearch, setCategory]);

	const onCategoryChange = React.useCallback(
		(category: Category) => {
			clearSearch();
			scrollRef.current?.scrollTo(0, 0);
			setCategory(category);
		},
		[clearSearch, setCategory],
	);

	const modalCtx = useModals();
	const { hasAccessToPromptHeadings } = queryResult.viewer.account.billingAccount;
	const onCreateClick = React.useCallback(
		() =>
			modalCtx
				.open<string | null>(({ close }) => (
					<Create
						close={close}
						initialTags={
							(Object.values(FixedCategory) as readonly string[]).includes(category) ? undefined : [category]
						}
						hasAccessToPromptHeadings={hasAccessToPromptHeadings}
						schemaVersion={window.CURRENT_NOTES_SCHEMA_VERSION}
					/>
				))
				.then(createdId => {
					if (createdId != null) setPreviewing(createdId);
				}),
		[modalCtx, setPreviewing, category, hasAccessToPromptHeadings],
	);

	const haveRecentlyUsed = React.useMemo(() => {
		return filterTemplates(noteTemplates, FixedCategory.RECENT, "").length !== 0;
	}, [noteTemplates]);

	const backFn = React.useMemo(() => {
		if (props.mode === "preview" && props?.hideBackButton) return;

		return () => {
			setPreviewing(undefined);
			setPerformScroll(true);
		};
	}, [props]);

	if (previewing) {
		return <Preview back={backFn} templateId={previewing} setPreviewing={setPreviewing} close={close} />;
	}

	const handleScroll = (event: React.UIEvent<HTMLDivElement> | undefined) => {
		if (!event) {
			return;
		}

		const target = event.target as HTMLDivElement;
		const { scrollTop } = target;
		scrollPosition.current = scrollTop;
	};

	return (
		<>
			<Modal.Header>
				<Modal.Title>Browse templates</Modal.Title>
				<Modal.HeaderActions onClose={closeModal} className={styles.headerActions}>
					{!isMobile && <HelpCenterIcon articleId="2423913" className={styles.helpLink} title="Templates" />}
				</Modal.HeaderActions>
				{isMobile && (
					<>
						<SearchBox value={search} onChange={setSearchFromEvent} onClear={clearSearch} />{" "}
						<CategoryList
							showRecent={haveRecentlyUsed}
							selectedCategory={category}
							onSelectionChange={onCategoryChange}
							tagCategories={allTags}
							search={search}
							onSearchChange={setSearchFromEvent}
							onSearchClear={clearSearch}
							dropdownViewOnMobile={true}
						/>
					</>
				)}
			</Modal.Header>
			<Modal.Body noPadding className={styles.modalBody}>
				{!isMobile && (
					<div className={styles.categoryListContainer}>
						<CategoryList
							showRecent={haveRecentlyUsed}
							selectedCategory={category}
							onSelectionChange={onCategoryChange}
							tagCategories={allTags}
							search={search}
							onSearchChange={setSearchFromEvent}
							onSearchClear={clearSearch}
							dropdownViewOnMobile={true}
						/>
					</div>
				)}
				<div className={styles.gridContainer} onScroll={handleScroll} ref={scrollRef}>
					{filteredTemplates.length > 0 ? (
						<TemplateGrid
							showCreateCard={!search && !isMobile && category !== FixedCategory.WORKSPACE}
							onClickCreate={onCreateClick}
							templates={filteredTemplates}
							preview={setPreviewing}
							onApply={close}
							selected={defaultSelected}
						/>
					) : search ? (
						<NoSearchResults query={search} />
					) : (
						<CategoryEmptyState
							category={category}
							canCreateWorkspaceTemplate={queryResult.viewer.canCreateWorkspaceTemplate}
							hasAccessToWorkspaceTemplates={queryResult.viewer.account.billingAccount.hasAccessToWorkspaceTemplates}
							subscriptionProductName={queryResult.viewer.account.billingAccount.subscriptionProductName}
							createTemplate={onCreateClick}
						/>
					)}
				</div>
			</Modal.Body>
		</>
	);
}

const Browse = withSuspense(<LoadingFallback />, BrowseInner);

const PreventCloseContext = React.createContext<null | ((doPrevent: boolean) => void)>(null);
PreventCloseContext.displayName = "PreventCloseContext";

function usePreventClose() {
	const setPreventClose = React.useContext(PreventCloseContext);
	React.useEffect(() => {
		setPreventClose?.(true);
		return () => setPreventClose?.(false);
	}, [setPreventClose]);
}

type NoteTemplateModalProps = {
	noteId?: string;
	/** The text for the primary buttons in the modal, e.g. "Select" or "Apply" */
	actionText?: string;
	beforeSubmit?: (selectedId: string) => Promise<boolean>;
	/**
	 * The template that is currently selected, i.e. has the blue border and
	 * checkmark and is pinned to the top of the template grid.
	 */
	defaultSelected?: string;
	/**
	 * The default category that should be selected
	 */
	defaultCategory?: Category;
} & (
	| { mode: "browse" }
	| {
			mode: "preview";
			hideBackButton?: boolean;
			/** The ID of a NoteTemplateNode that should be previewed initially. */
			defaultPreviewing: string;
	  }
);

export const NoteTemplateModal = ({
	onClose,
	actionText = "Apply",
	beforeSubmit,
	...props
}: NoteTemplateModalProps & {
	onClose: (result: string | undefined) => void;
}) => {
	const [preventClose, setPreventClose] = React.useState(false);

	const onDismiss = React.useCallback(
		(result?: string) => {
			if (!preventClose) {
				if (beforeSubmit && result) {
					beforeSubmit(result).then(yes => yes && onClose(result));
				} else {
					onClose(result);
				}
			}
		},
		[preventClose, onClose, beforeSubmit],
	);

	return (
		<PreventCloseContext.Provider value={setPreventClose}>
			<NoteIdContext.Provider value={props.noteId}>
				<ActionTextContext.Provider value={actionText}>
					<Modal
						// eslint-disable-next-line react/jsx-no-bind
						onClose={() => onDismiss()}
						className={styles.modal}
						data-test-id="note-template-modal"
					>
						{props.mode === "browse" ? (
							<Browse {...props} close={onDismiss} />
						) : (
							<Browse {...props} mode="preview" templateId={props.defaultPreviewing} close={onDismiss} />
						)}
					</Modal>
				</ActionTextContext.Provider>
			</NoteIdContext.Provider>
		</PreventCloseContext.Provider>
	);
};

/**
 * Returns a function used to open a note template modal in the current modal
 * context.
 */
export function useNoteTemplateModal() {
	const ctx = useModals();

	return React.useCallback(
		function openNoteTemplateModal(opts: NoteTemplateModalProps) {
			return ctx.open<string | undefined>(({ close }) => <NoteTemplateModal onClose={close} {...opts} />);
		},
		[ctx],
	);
}

export interface NoteTemplateModalOpenerRef {
	open: ReturnType<typeof useNoteTemplateModal>;
}

/** An adapter to enable class components to use `useNoteTemplateModal`.  */
export const NoteTemplateModalOpener = React.forwardRef((_props, ref: React.Ref<NoteTemplateModalOpenerRef>) => {
	const open = useNoteTemplateModal();

	React.useImperativeHandle(ref, () => ({ open }), [open]);

	return null;
});

NoteTemplateModalOpener.displayName = "NoteTemplateModalOpener";
